import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useCallback } from 'react'
import toast from 'react-hot-toast'

import CircleCheckFill from '@/components/icons/circle-check-fill.svg'
import Copy from '@/components/icons/copy.svg'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'

export interface ApiApplySuccessProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  apiKey?: string
  apiSecret?: string
}

export default function ApiApplySuccess({
  open,
  onOpenChange,
  apiKey,
  apiSecret,
}: ApiApplySuccessProps) {
  const onClose = useCallback(() => onOpenChange(false), [onOpenChange])
  const onCopyApiKey = useCallback(async () => {
    if (apiKey) {
      await navigator.clipboard.writeText(apiKey)
      toast.success('AxGrad API key copied')
    }
  }, [apiKey])

  const onCopyApiSecret = useCallback(async () => {
    if (apiSecret) {
      await navigator.clipboard.writeText(apiSecret)
      toast.success('AxGrad API secret copied')
    }
  }, [apiSecret])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[40rem]" showClose={false}>
        <VisuallyHidden>
          <DialogTitle>API Key applied successfully!</DialogTitle>
        </VisuallyHidden>
        <div className="grid gap-6">
          <CircleCheckFill className="fill-success [&_path]:fill-white size-20 m-auto" />
          <div className="text-center">
            <div className="text-xl font-medium mb-2">
              API Key applied successfully!
            </div>
            <div className="text-sm text-secondary-foreground mb-4">
              Please make sure you securely store your API Key and Secret and avoid sharing them
              with others.
            </div>
            <div className="text-xs flex items-center justify-center gap-2 mb-1">
              <div>API Key:</div>
              <div className="text-secondary-foreground">{apiKey}</div>
              <Copy
                className="size-3 cursor-pointer fill-tertiary-foreground"
                onClick={onCopyApiKey}
              />
            </div>
            <VisuallyHidden className="text-xs flex items-center justify-center gap-2">
              <div>API Secret:</div>
              <div className="text-secondary-foreground">{apiSecret}</div>
              <Copy
                className="size-3 cursor-pointer fill-tertiary-foreground"
                onClick={onCopyApiSecret}
              />
            </VisuallyHidden>
          </div>
          <Button variant="outline" className="w-20 m-auto" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
